














































































































import { AxiosError } from 'axios';
import Vue from 'vue';
import { PropType } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import {
  IAttachment,
  IUser,
  ITimeOffRequest,
  IMentionAttachmentComment,
  TimeOffRequestComment,
  TimeOffRequestCommentAttachment,
  TimeOffRequestCommentFieldError
} from '@/models';
import { ErrorManager } from '@/models/error';
import { isHtmlTextEmpty } from '@/utils/helpers';

import CommentAttachmentList from '@/components/common/CommentAttachmentList.vue';
import QuillEditor from '../common/QuillEditor.vue';
import TimeOffRequestCommentListItem from './TimeOffRequestCommentListItem.vue';

export default Vue.extend({
  name: 'TimeOffRequestCommentList',

  components: {
    CommentAttachmentList,
    QuillEditor,
    TimeOffRequestCommentListItem
  },

  props: {
    timeOffRequest: {
      type: Object as PropType<ITimeOffRequest>,
      required: true
    }
  },

  data() {
    return {
      loading: false,
      isEditing: false,

      commentLoading: false,
      fieldError: new TimeOffRequestCommentFieldError(),

      commentErrorMessage: '',
      commentErrorMessageDetail: '',

      comment: new TimeOffRequestComment(),

      mentionsLoading: false,

      attachments: [] as IAttachment[],
      timeoffRequestCommentValid: false
    };
  },

  computed: {
    ...mapGetters({
      comments: 'timeOffRequest/getTimeOffRequestComments',
      mentionableUsers: 'timeOffRequest/getTimeOffRequestMentionableUsers',
      getLastCreatedTimeOffRequestCommentId:
        'timeOffRequest/getLastCreatedTimeOffRequestCommentId'
    }),

    canSaveComment(): boolean {
      return !isHtmlTextEmpty(this.comment.text) && !this.commentLoading;
    }
  },

  mounted() {
    this.loading = true;
    this.fetchTimeOffRequestComments(this.timeOffRequest.id)
      .catch(error => {
        if (error.response) {
          if (error.response.data.detail) {
            this.commentErrorMessageDetail = error.response.data.detail;
          }
        }
        this.commentErrorMessage = ErrorManager.extractApiError(error);

        this.updateErrorMessage();
      })
      .finally(() => {
        this.loading = false;
      });
  },

  methods: {
    ...mapActions({
      fetchTimeOffRequestComments: 'timeOffRequest/fetchTimeOffRequestComments',
      createTimeOffRequestComment: 'timeOffRequest/createTimeOffRequestComment',
      fetchTimeOffRequestCommentsMentionableUsers:
        'timeOffRequest/fetchTimeOffRequestCommentsMentionableUsers',
      addTimeOffRequestCommentAttachments:
        'timeOffRequest/addTimeOffRequestCommentAttachments'
    }),

    addBaseAttachments(files: File[]) {
      this.attachments.push(
        ...files.map(
          file => new TimeOffRequestCommentAttachment({ file: file })
        )
      );
    },

    updateErrorMessage() {
      this.$emit('setErrorMessage', {
        errorMessage: this.commentErrorMessage,
        errorMessageDetail: this.commentErrorMessageDetail
      });
    },

    clearAllErrors(): void {
      this.fieldError.clearErrors();
      this.commentErrorMessage = '';
      this.commentErrorMessageDetail = '';

      this.updateErrorMessage();
    },

    addTimeOffRequestComment() {
      this.clearAllErrors();

      this.commentLoading = true;

      const parsedCommentMentionText = TimeOffRequestComment.parseHtmlToMentionText(
        this.comment.text
      );

      return this.createTimeOffRequestComment({
        timeOffId: this.timeOffRequest.id,
        comment: { ...this.comment, text: parsedCommentMentionText }
      })
        .then(async () => {
          this.comment = new TimeOffRequestComment();
          if (this.attachments.length !== 0) {
            await this.addTimeOffRequestCommentAttachments({
              timeOffId: this.timeOffRequest.id,
              commentId: this.getLastCreatedTimeOffRequestCommentId,
              attachments: this.attachments
            });
            this.attachments = [];
          }
        })
        .catch(error => {
          if (error.response) {
            // client received an error response that falls out of range 2xx
            if (
              TimeOffRequestCommentFieldError.isTimeOffRequestCommentFieldError(
                error.response.data
              )
            ) {
              this.fieldError = new TimeOffRequestCommentFieldError(
                error.response.data
              );
            }

            if (error.response.data.detail) {
              this.commentErrorMessageDetail = error.response.data.detail;
            }
          }
          this.commentErrorMessage = ErrorManager.extractApiError(error);

          this.updateErrorMessage();
        })
        .finally(() => {
          this.commentLoading = false;
        });
    },

    async loadMentionableUsers() {
      this.clearAllErrors();

      this.mentionsLoading = true;
      try {
        await this.fetchTimeOffRequestCommentsMentionableUsers(
          this.timeOffRequest.id
        );
      } catch (error) {
        this.commentErrorMessage = ErrorManager.extractApiError(
          error as AxiosError
        );
      } finally {
        this.mentionsLoading = false;
      }
    }
  }
});
